/**
 * New quote record (SA-401 D28/D32/D35): a quote-class record born from a
 * form over the agenda's published slots, all optional, with an optional
 * paste → Extract that pre-fills fields (marked "extracted"; editing flips
 * them to "entered"). Submit creates the record; it does NOT start the
 * process — Start is a press on the record's Process panel.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalTitle, NativeSelect, Textarea,
} from '@/components/ui'
import { FieldBlock, type FieldBlockField } from '@/components/process/FieldBlock'
import { isBlank } from '@/components/process/fieldState'
import { extractedSource } from '@/components/process/fieldStateText'
import { ContactSearch, type ContactHit } from '@/components/orders/ContactSearch'
import { useFeature } from '@/hooks'
import { apiClient } from '@/services/apiClient'
import type { IntakeAgenda, IntakeExtraction, IntakeField, IntakeProvenance, QuoteRecordCreatePayload } from '@/types/commerce'

type Values = Record<string, unknown>
type Provenance = Record<string, IntakeProvenance>

const asBlockField = (f: IntakeField): FieldBlockField =>
  ({ id: f.id, label: f.label, type: f.type, options: f.options ?? null, optional: !f.required, help: f.help ?? null,
     alternatives: f.alternatives })

function filled(v: unknown): boolean {
  return v !== null && v !== undefined && v !== ''
}

export function NewQuoteRecordModal({ open, onOpenChange, agendas, onExtract, onCreate }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  agendas: IntakeAgenda[]
  onExtract: (agenda: string, text: string, personaId?: string) => Promise<IntakeExtraction>
  onCreate: (payload: QuoteRecordCreatePayload) => Promise<void>
}) {
  const crmEnabled = useFeature('crm')
  const [agendaId, setAgendaId] = useState(agendas[0]?.workflow_id ?? '')
  const agenda = useMemo(() => agendas.find((a) => a.workflow_id === agendaId) ?? agendas[0], [agendas, agendaId])
  const [personas, setPersonas] = useState<{ id: string; name: string }[]>([])
  const [personaId, setPersonaId] = useState('')
  const [contact, setContact] = useState<ContactHit | null>(null)
  const [pasteText, setPasteText] = useState('')
  const [values, setValues] = useState<Values>({})
  const [provenance, setProvenance] = useState<Provenance>({})
  const [extracted, setExtracted] = useState<Record<string, unknown>>({})
  const [notes, setNotes] = useState('')
  const [extracting, setExtracting] = useState(false)
  const [extractError, setExtractError] = useState<string | null>(null)
  const [extractSummary, setExtractSummary] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)

  // Review fix (Ruling 5): OrdersPage keeps this component mounted after
  // Create (it renders on intakeAgendas.length > 0, not on `open`), so a
  // staff agenda/persona pick would otherwise leak into the next record and
  // D35's "preselected to the single bound persona" contract would only
  // hold on the very first open. Reset agenda + persona on every `open`
  // transition — deliberately keyed on `open`/`agendas`, NOT on `agenda`
  // (the derived selection), so a mid-session agenda switch is never
  // fought back to the default.
  useEffect(() => {
    if (!open) return
    setAgendaId(agendas[0]?.workflow_id ?? '')
    setPersonaId('')
  }, [open, agendas])

  useEffect(() => {
    if (!open) return
    // F2/D31: every field, including booleans, seeds `null` (unset) rather
    // than `false` — an untouched boolean must mean "unknown", not an
    // authored "no"; `filled()` at submit already excludes `null` from
    // `slots` while keeping an explicit `false` (a real toggle-off).
    setValues(Object.fromEntries((agenda?.fields ?? []).map((f) => [f.id, null])))
    setProvenance({}); setExtracted({}); setPasteText(''); setExtractError(null); setExtractSummary(null); setRequestError(null); setNotes(''); setContact(null)
  }, [open, agenda])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await apiClient.fetch('/api/personas')
        if (!res.ok || cancelled) return
        const list = (await res.json()) as { id: string; name: string }[]
        if (cancelled) return
        setPersonas(list.map((p) => ({ id: p.id, name: p.name })))
        // D35: preselect the persona whose bundle binds this agenda, when exactly one does.
        const bound = (agenda?.bound_persona_ids ?? []).filter((id) => list.some((p) => p.id === id))
        setPersonaId((prev) => prev || (bound.length === 1 ? bound[0] : ''))
      } catch { /* persona attach is optional */ }
    })()
    return () => { cancelled = true }
  }, [open, agenda])

  const setField = (id: string, v: unknown) => {
    setValues((s) => ({ ...s, [id]: v }))
    setProvenance((p) => (p[id] === 'extracted' ? { ...p, [id]: 'entered' } : p))
  }

  // SA-1196: a restored value is the extracted one again, marked extracted.
  const restoreExtracted = (id: string, v: unknown) => {
    setValues((s) => ({ ...s, [id]: v }))
    setProvenance((p) => ({ ...p, [id]: 'extracted' }))
  }

  const handleExtract = async () => {
    if (!agenda || !pasteText.trim()) return
    setExtracting(true); setExtractError(null); setExtractSummary(null)
    try {
      const result = await onExtract(agenda.workflow_id, pasteText, personaId || undefined)
      setValues((s) => ({ ...s, ...result.values }))
      setProvenance((p) => ({ ...p, ...Object.fromEntries(Object.keys(result.values).map((k) => [k, 'extracted' as const])) }))
      setExtracted((e) => ({ ...e, ...result.values }))
      const n = Object.keys(result.values).length
      const dropped = result.dropped.map((d) => `${d.slot}: ${d.reason}`).join(', ')
      setExtractSummary(`${n} field${n === 1 ? '' : 's'} read from the text${result.dropped.length ? ` · ${result.dropped.length} value${result.dropped.length === 1 ? '' : 's'} couldn't be used (${dropped})` : ''}`)
    } catch (e) {
      setExtractError((e as Error).message)
    } finally {
      setExtracting(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!agenda) return
    setSaving(true); setRequestError(null)
    const slots = Object.fromEntries(Object.entries(values).filter(([, v]) => filled(v)))
    const prov: Provenance = Object.fromEntries(Object.keys(slots).map((k) => [k, provenance[k] ?? 'entered']))
    const payload: QuoteRecordCreatePayload = {
      agenda: agenda.workflow_id,
      ...(personaId ? { persona_id: personaId } : {}),
      ...(contact ? { contact_id: contact.id } : {}),
      slots, provenance: prov,
      ...(pasteText.trim() ? { source: { text: pasteText } } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    }
    try {
      await onCreate(payload)
      onOpenChange(false)
    } catch (err) {
      setRequestError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="lg">
        <ModalHeader><ModalTitle>New quote record</ModalTitle></ModalHeader>
        <form noValidate onSubmit={handleSubmit}>
          <ModalBody className="space-y-6">
            {agendas.length > 1 && (
              <NativeSelect label="Agenda" id="nqr-agenda" value={agendaId} onChange={(e) => setAgendaId(e.target.value)}>
                {agendas.map((a) => <option key={a.workflow_id} value={a.workflow_id}>{a.name}</option>)}
              </NativeSelect>
            )}
            <section className="space-y-3">
              <NativeSelect label="Persona" id="nqr-persona" value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
                <option value="">No persona</option>
                {personas.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </NativeSelect>
              {crmEnabled && (contact ? (
                <div className="flex items-center justify-between text-sm rounded-lg border border-border px-3 py-2">
                  <span>Linked: <span className="font-medium">{contact.display_name || contact.channel_user_id}</span></span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setContact(null)}>Unlink</Button>
                </div>
              ) : <ContactSearch onSelect={setContact} />)}
            </section>
            <section className="space-y-2">
              <label htmlFor="nqr-paste" className="text-sm font-medium">Paste the inquiry</label>
              <Textarea id="nqr-paste" rows={6} value={pasteText} onChange={(e) => setPasteText(e.target.value)}
                placeholder="Paste the forwarded email, chat message or notes — every field it states will be read into the form." />
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" size="sm" disabled={extracting || !pasteText.trim()} onClick={handleExtract}>
                  {extracting ? 'Extracting…' : 'Extract fields'}
                </Button>
                {extractSummary && <span className="text-sm text-muted-foreground">{extractSummary}</span>}
              </div>
              {extractError && <p className="text-sm text-destructive">{extractError}</p>}
            </section>
            <section className="space-y-3">
              {(agenda?.fields ?? []).map((f) => {
                // SA-1196 (spec 5.8): the pre-fill is what the extraction put there; its source rides it.
                const pre = isBlank(extracted[f.id]) ? null : { value: extracted[f.id] }
                return (
                  <FieldBlock key={f.id} idPrefix="intake" field={asBlockField(f)} value={values[f.id]}
                    onChange={(v) => setField(f.id, v)} onRestore={(v) => restoreExtracted(f.id, v)}
                    prefill={pre} source={pre ? extractedSource('paste', null) : null} />
                )
              })}
            </section>
            <div>
              <label htmlFor="nqr-notes" className="mb-1.5 block text-sm font-medium text-foreground">Notes</label>
              <Textarea id="nqr-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            {requestError && <p className="text-sm text-destructive">{requestError}</p>}
          </ModalBody>
          <ModalFooter className="flex-wrap items-center">
            {/* SA-469 (spec §3): the form births the record; Start is a press on the record (SA-401 D28).
                SA-476 (spec D2): the start is promised only when the agenda read names the process it would start. */}
            <p className="mr-auto text-xs text-muted-foreground">
              {agenda?.process_name
                ? `Creates the record only. The ${agenda.process_name} process starts when you press Start on the record.`
                : 'Creates the record only. No process starts from this intake.'}
            </p>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !agenda}>{saving ? 'Creating…' : 'Create record'}</Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
